import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import EnvironmentProvider, { useEnvironment } from './EnvironmentProvider'

type TRequests<T = unknown> = Record<string, (options: AxiosRequestConfig<T>) => Promise<T>>

export interface IRequestProvider<T = unknown> {
  requests: TRequests<T>
}

/**
 * Interface for the api object
 * @interface IApi
 * @param id - The id of the api. Used to identify the api in the environment requests object
 * @param url - The api url
 */
interface IApi {
  id: string
  url: string
}

const DEAFULT_API: IApi = {
  id: 'default',
  url: 'http://localhost:3000/api',
}

const getRequest = <T,>({ instance }: { instance: AxiosInstance }) =>
  async (options: AxiosRequestConfig<T>) => {
    const onSuccess = (response: AxiosResponse<T, T>) => response.data
    const onError = (error: AxiosError) => Promise.reject(error.response)
    return instance(options)
      .then(onSuccess)
      .catch(onError)
  }

/**
 * Adds an axios instance to the requests object in the environment
 */
const RequestProvider = <T = unknown,>({ api = DEAFULT_API, ...props }) => {
  const { requests = {} } = useEnvironment<IRequestProvider<T>>()

  const instance = axios.create({
    baseURL: api.url,
  })
  requests[api.id] = getRequest({ instance })

  return (
    <EnvironmentProvider
      env={{ requests }}
      {...props}
    />
  )
}

export default RequestProvider
